import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import {
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import type { BulkUploadLeadsRequest } from '@/types/employment/BulkUploadLeadsRequest';

import Loader from './Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isBulkUploadTemplate?: boolean;
  setIsBulkUploadTemplate?: (value: boolean) => void;
}

export interface TaxExtensionsBulkUploadTemplateProps {
  isBulkUploadTemplate: boolean;
  setIsBulkUploadTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  formType?: '4868' | '7004';
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
      {children}
      {onClose ? (
        <Tooltip title="Close" arrow>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              p: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </DialogTitle>
  );
}

const TaxExtensionsBulkUploadTemplate = (
  props: TaxExtensionsBulkUploadTemplateProps
) => {
  const formType = props.formType || '4868';
  const [isLoading, setIsLoading] = useState(false);

  /** ✅ Fix: properly typed schema */
  const validationSchema = Yup.object().shape({
    UserName: Yup.string().required('Name is required'),
    EmailAddress: Yup.string()
      .required('Email Address is required')
      .email('Enter valid Email Address'),
  });

  const defaultValue: BulkUploadLeadsRequest = {
    EmailAddress: '',
    UserName: '',
    Category: '',
    Token: '',
  };

  const { handleSubmit, control, reset } = useForm<BulkUploadLeadsRequest>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      BulkUploadLeadsRequest,
      any
    >,
    defaultValues: defaultValue,
  });

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = () => {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      () => {}
    );
  }, []);

  const onSubmit = async (data: BulkUploadLeadsRequest) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: BulkUploadLeadsRequest = data;
          postData.Category = `Form${formType}BulkUploadTemplate`;
          postData.Token = token;
          const address = `/api/bulkuploadlead/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData === true) {
            reset(defaultValue);
            // Trigger file download
            const link = document.createElement('a');
            link.href = `https://s3.us-east-1.amazonaws.com/taxzerone.dev/StaticFiles/Extension/BulkUploadTemplates/2025/TaxZerone_Form_${formType}_Template.xlsx`;
            link.download = `TaxZerone_Form_${formType}_Template.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            props.setIsBulkUploadTemplate(false);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
  };

  const handleClose = () => {
    props.setIsBulkUploadTemplate(false);
  };

  useEffect(() => {
    reset(defaultValue);
  }, [props.isBulkUploadTemplate]);

  return (
    <BootstrapDialog
      open={props.isBulkUploadTemplate}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <Loader isLoading={isLoading} />

      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }}>
          {/* LEFT PANEL */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              background: '#0f172a', // deep dark blue
              color: '#ffffff',
              p: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* GLOW EFFECT */}
            <Box
              sx={{
                position: 'absolute',
                width: 300,
                height: 300,
                background:
                  'radial-gradient(circle, rgba(29,169,74,0.25), transparent 70%)',
                top: -80,
                right: -80,
                filter: 'blur(20px)',
              }}
            />

            {/* ICON */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <DownloadForOfflineOutlinedIcon
                sx={{
                  fontSize: 48,
                  color: '#1da94a',
                  filter: 'drop-shadow(0 0 8px rgba(29,169,74,0.6))',
                }}
              />
            </Box>

            {/* TEXT */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                lineHeight: 1.4,
                maxWidth: 320,
              }}
            >
              Download <br />
              the{' '}
              <span style={{ fontWeight: 600, color: '#1da94a' }}>
                Form {formType}
              </span>{' '}
              Bulk Upload Template Now!
            </Typography>
          </Box>

          {/* RIGHT PANEL */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              flex: 1.2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 2, md: 4 },
              background: 'linear-gradient(135deg, #f5f7fa, #e4efe9)',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 480,
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                backgroundColor: '#ffffff',
                boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              }}
            >
              <Typography
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  fontSize: 18,
                  textAlign: 'center',
                  color: '#333',
                }}
              >
                Get Form {formType} template for free – just enter your details!
              </Typography>

              {/* NAME */}
              <Controller
                name="UserName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="*Name"
                    fullWidth
                    variant="outlined"
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                      },
                    }}
                  />
                )}
              />

              {/* EMAIL */}
              <Controller
                name="EmailAddress"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="*Email Address"
                    fullWidth
                    variant="outlined"
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                      },
                    }}
                  />
                )}
              />

              {/* BUTTON */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  type="submit"
                  sx={{
                    width: '100%',
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: 16, sm: 18 },
                    borderRadius: 3,
                    background:
                      'linear-gradient(135deg, rgb(33, 62, 153), rgb(60, 90, 190))',
                    boxShadow: '0 8px 20px rgba(33, 62, 153, 0.35)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, rgb(28, 52, 130), rgb(50, 80, 170))',
                      boxShadow: '0 10px 24px rgba(33, 62, 153, 0.45)',
                    },
                  }}
                >
                  Download Now
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default TaxExtensionsBulkUploadTemplate;
